import * as d3 from 'd3';
import _ from 'underscore';

import {FragmentResult} from 'biojs/FragmentResult';
import {HomologyAwareFragmentResultData} from 'molbio/maps/circular/HomologyUtil';
import * as sequtil from 'molbio/SeqUtil';
import {toStandardAngle} from 'util/geometry/GeometryUtil';

/**
 * Gets the increment between each axis numbering label for a sequence, as close to the given ideal
 * number of labels as possible.
 * @param rangeLength The length of the range for which we are getting labels
 * @param idealNumLabels The 'ideal' number of labels to show
 */
export function axisSpacing(rangeLength: number, idealNumLabels: number = 8): number {
  const targetSpacing = rangeLength / idealNumLabels;
  // Get the next largest power of 10 after rangeLength; e.g., a rangeLength of 80 will produce 100,
  // a rangeLength of 101 will produce 1000
  const pow10 = Math.pow(10, Math.floor(Math.log(targetSpacing) / Math.LN10) + 1);
  // We want to put ticks at roundish numbers - 25, 50, 250, 500, etc. Find the roundish number that gets us
  // closest to the ideal number of labels.
  return _.chain([pow10 / 4, pow10 / 2, pow10])
    .filter((x) => x % 1 === 0)
    .min((x) => Math.abs(x - targetSpacing))
    .value();
}

/**
 * Get the color for the tick at the given index
 * @param fragmentResults If this tick is being rendered on a sequence that is an assembly construct, the
 *                        fragments in that construct
 * @param piecesData If this tick is being rendered on a sequence that is an assembly construct, the bounds of
 *                   each fragment in that construct
 * @param standardAngle Angle in radians between the sequence origin and this tick
 * @param startIndex The starting index of the sequence
 * @param tick The number for the tick
 * @param useBaseWidthStripe Whether stripes should be clamped to the width of a single base
 */
export function getTickColor({
  fragmentResults,
  piecesData,
  standardAngle,
  startIndex,
  tick,
  useBaseWidthStripe,
}: {
  fragmentResults: Array<FragmentResult>;
  piecesData: Array<HomologyAwareFragmentResultData> | null;
  standardAngle: number;
  startIndex: number;
  tick: number;
  useBaseWidthStripe: boolean;
}): string {
  if (piecesData != null) {
    if (useBaseWidthStripe) {
      // Make the stripe the color of the 'next' base, which is what users think of the number as
      // corresponding to.
      for (const fragmentResult of fragmentResults) {
        const modelBase = sequtil.displayToModel(tick, startIndex);
        if (sequtil.baseInRange(modelBase, fragmentResult.start()!, fragmentResult.end()!)) {
          const otherColor = fragmentResult.homologyRegionOtherColor();
          if (
            otherColor != null &&
            // Start with the 'other' color.
            (modelBase - fragmentResult.start()!) % 2 === 0
          ) {
            return otherColor;
          } else {
            return fragmentResult.color()!;
          }
        }
      }
    } else {
      // Stripes do not correspond nicely to base pair indices and we have to do arithmetic :(
      for (const {pieceStartAngle, pieceEndAngle, color} of piecesData) {
        const standardStart = toStandardAngle(pieceStartAngle);
        const standardEnd = toStandardAngle(pieceEndAngle);
        const isTickContainedInNonCrossOriginPiece =
          standardStart < standardEnd && standardStart < standardAngle && standardAngle <= standardEnd;
        // We have to check both sides of the origin separately in a cross-origin piece. That
        // 0 < standardAngle < 2 * PI is enforced by toStandardAngle.
        const isTickContainedInCrossOriginPiece =
          standardEnd < standardStart && (standardStart < standardAngle || standardAngle <= standardEnd);
        if (
          isTickContainedInNonCrossOriginPiece ||
          isTickContainedInCrossOriginPiece ||
          // Piece includes entire sequence - single-slot assembly wizard case
          standardStart === standardEnd
        ) {
          return color;
        }
      }
    }
  }
  return 'black';
}

/**
 * Get the set of ticks to display on the circular axis, based on how much of it is visible.
 * @param fullCircleView Whether the entire plasmid is visible
 * @param leftBP The index of the leftmost visible base pair
 * @param rightBP The index of the rightmost visible base pair
 * @param sequenceLength The length of the sequence
 */
export function getTicks({
  fullCircleView,
  leftBP,
  rightBP,
  sequenceLength,
}: {
  fullCircleView: boolean;
  leftBP: number;
  rightBP: number;
  sequenceLength: number;
}): Array<number> {
  let ticks: Array<number>;
  // The entire plasmid is visible
  if (fullCircleView) {
    const labelIncrement = axisSpacing(sequenceLength);
    // TODO: we shouldn't need d3 for this but we don't have a range util with a step
    ticks = d3.range(labelIncrement, sequenceLength + 1, labelIncrement);
  } else if (leftBP > rightBP) {
    // viewed portion of sequence crosses origin
    const range = sequenceLength - leftBP + rightBP;
    const labelIncrement = axisSpacing(range);
    // The outermost rendered base pairs might not line up nicely with a label increment; subtract the
    // remainder to get an even number for the left- and rightmost ticks
    const leftmostTick = leftBP - (leftBP % labelIncrement);
    const rightmostTick = rightBP - (rightBP % labelIncrement) + labelIncrement;
    ticks = [
      ...d3.range(leftmostTick, sequenceLength - labelIncrement / 2, labelIncrement),
      ...d3.range(labelIncrement, rightmostTick, labelIncrement),
    ];
  } else {
    const range = rightBP - leftBP;
    const labelIncrement = axisSpacing(range);
    const leftmostTick = leftBP - (leftBP % labelIncrement);
    const rightmostTick = rightBP - (rightBP % labelIncrement) + labelIncrement;
    ticks = d3.range(leftmostTick, rightmostTick + 1, labelIncrement);
  }
  return ticks;
}
